import { last, sortBy } from "lodash";
import { profileHash } from "PFCore/helpers/profile_hash";
import { CustomValue, ProfileExport } from "PFTypes";
import { capitalize, humanize } from "underscore.string";

import { EY_CERTIFICATES_FIELD_NAMES } from "../helpers/custom_pptx_template";
import { getSectors } from "../helpers/get_sectors";
import { sortInternalExperiences } from "../helpers/sort_internal_experiences";
import { ContactInformationItem, CustomType, Position } from "../parts/ey_pptx_preview/types";
import {
  CHARS_PER_POINT,
  defaultCompanyName,
  EMPTY_LINE,
  inchToPts,
  ptsToInch,
  spaceSize
} from "./constants";
import { sidebarHeaderOptions, textOptions } from "./use_ey_pptx_options";

export type PptxgenJsTextOptions = {
  h?: number;
  w?: number;
  y?: number;
  x?: number;
  color?: string;
  indent?: number;
  bullet?: { indent: number };
  softBreakBefore?: boolean;
  lineSpacing?: number;
  autoFit?: boolean;
  bold?: boolean;
  transparency?: number;
  fontSize?: number;
  breakLine?: boolean;
  underline?: { color?: string; fontFace?: string; fontSize?: number; style?: string };
};
export type TextItem = { text: string; options: PptxgenJsTextOptions };
export type ProfileExportFormatted = {
  id: number;
  enabled: boolean;
  email: string;
  name: string;
  jobTitle: string;
  companyName: string;
  summary: string;
  contactInformationItems: {
    type: string;
    text: string;
  }[];
  positions: { items: Position[] };
  customTypes: CustomType[];
  _originalData: ProfileExport;
};
// adjusted to fit inteenal experiences indent workaround
const internalExperienceDescriptionIndent = 22;

export const formatExperienceText = (
  formatDate: (str: string) => string,
  jobTitle?: string,
  startDate?: string,
  endDate?: string
) => {
  const jobTitleFormatted = jobTitle ? jobTitle : "";
  const startDateFormatted = startDate ? formatDate(startDate) : "";
  const endDateFormatted = startDate && endDate ? formatDate(endDate) : "";
  const startDateDelimiter = jobTitle && startDate ? " - " : "";
  const endDateDelimiter = startDate && endDate ? " - " : "";

  return [jobTitleFormatted, startDateDelimiter, startDateFormatted, endDateDelimiter, endDateFormatted].join(
    ""
  );
};

export const filterDeselectedSkills = (skills: CustomValue[], skillsType: CustomType) =>
  skills.filter(({ text }) =>
    skillsType.text
      .split("\r")
      .map((text) => text.trim())
      .includes(text)
  );

const getCharsInLine = (options: PptxgenJsTextOptions) =>
  inchToPts(
    (options.w || 0) -
      ptsToInch(options.bullet?.indent || options.indent || 0) -
      (options.softBreakBefore ? ptsToInch(20) : 0)
  ) *
    CHARS_PER_POINT *
    (8 / (options.fontSize || 8)) -
  inchToPts(2 * spaceSize);

const splitEveryN = (str: string, len: number, arr: string[] = []): string[] => {
  if (str.length === 0) {
    return arr;
  }

  arr.push(str.slice(0, len));
  return splitEveryN(str.slice(len), len, arr);
};

const getLinesCount = (text: string, options: PptxgenJsTextOptions): number => {
  const textArray = text.replace(/\r/g, "").split(/\n/);
  const charsInLine = getCharsInLine(options);
  const newLines: string[][] = textArray.map((txt) => {
    if (txt.length <= charsInLine) {
      return [txt];
    }
    const words = txt
      .split(" ")
      .map((word) => {
        if (word.length <= charsInLine) {
          return word;
        }
        return splitEveryN(word, charsInLine);
      })
      .flat();

    return words.reduce((acc, curr) => {
      const lastItem = last(acc);
      if (lastItem) {
        const newItem = `${lastItem} ${curr}`;
        if (newItem.length <= charsInLine) {
          return [...acc.slice(0, -1), newItem];
        }
        return [...acc, curr];
      }
      return [curr];
    }, []);
  });

  return newLines.map(({ length }) => length).reduce((acc, curr) => acc + curr, 0);
};

const splitParagraph = (text: string, options: PptxgenJsTextOptions) => {
  const charsInLine = getCharsInLine(options);
  const words = text.split(" ");
  const lines = words.reduce((acc: TextItem[], curr) => {
    const lastItem = last(acc);
    if (!lastItem) {
      return [{ text: curr, options }];
    }
    const newText = `${lastItem.text} ${curr}`;
    if (newText.length > charsInLine) {
      return acc.concat({ text: curr, options });
    }
    return acc.slice(0, -1).concat({ text: newText, options });
  }, []);

  return lines;
};

export const limitText = (text: string, options: PptxgenJsTextOptions): TextItem[] => {
  const textArray: TextItem[] = text
    .trim()
    .replace(/\r/g, "")
    .split(/\n/)
    .map((text) => ({ text, options: {} }));

  return limitLines(textArray, options);
};

const limitLines = (textArray: TextItem[], options: PptxgenJsTextOptions): TextItem[] => {
  const maxLines = Math.floor(
    (inchToPts(options.h) - (3 / 2) * inchToPts(spaceSize)) / (options.lineSpacing || 10)
  );
  const linesPerText = textArray.map((row) => getLinesCount(row.text, { ...options, ...row.options }));
  const lastTextIndex = linesPerText.findIndex((_, index, arr) => {
    const subArray = arr.slice(0, index + 1);
    const sum = subArray.reduce((acc, curr) => acc + curr, 0);
    return sum > maxLines;
  });

  if (lastTextIndex === 0) {
    return limitLines(splitParagraph(textArray[0].text, options), options);
  }

  return lastTextIndex === -1 ? textArray : textArray.slice(0, lastTextIndex);
};

export const createCustomFieldContent = (
  customTypes: CustomType[],
  names: string[] = [],
  options: PptxgenJsTextOptions
): TextItem[] => {
  const values = names
    .reduce((acc, name) => {
      const customType = customTypes.find(({ type }) => type.name === name);
      if (customType) {
        const vals = customType.text ? customType.text.split("\r ") : [];
        return [...acc, ...vals];
      }
      return acc;
    }, [])
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const limitedFields = limitText(values.join("\n"), { ...options });

  if (values.length === 0) {
    return [];
  }

  return limitedFields.map((item) => ({
    text: item.text,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));
};

export const createCustomField = (
  customTypes: CustomType[],
  names: string[],
  title: string,
  options: PptxgenJsTextOptions
): TextItem[] => {
  const content = createCustomFieldContent(customTypes, names, options);
  if (!content.length) {
    return [];
  }

  return [
    {
      text: title,
      options: {
        ...sidebarHeaderOptions,
        autoFit: true
      }
    },
    ...content
  ];
};

export const createPositions = (
  positions: { header: string; items: Position[] },
  internalExperiences: ProfileExport["internal_experiences"] = [],
  options: PptxgenJsTextOptions,
  formatDate: (str: string) => string
): TextItem[] => {
  if (!positions?.items || positions.items.length === 0) {
    return [];
  }

  const sortedInternalExperiences = sortInternalExperiences(internalExperiences);

  const orderedPositions = sortBy(positions.items, (pos) => -Number(pos.main));
  const positionsWithInternalExperiences = orderedPositions.map((item) => {
    const internalExperiencesForPosition = (sortedInternalExperiences || []).filter(
      ({ metadata }) => metadata.position_id === item.id
    );
    return {
      ...item,
      internalExperiences: internalExperiencesForPosition
    };
  });

  const output: TextItem[] = [];
  positionsWithInternalExperiences.forEach(
    ({ jobTitle, companyName, summary, internalExperiences, startDate, endDate }) => {
      output.push({
        text: companyName || defaultCompanyName,
        options: {
          bold: true,
          softBreakBefore: true
        }
      });
      output.push({
        text: formatExperienceText(formatDate, jobTitle, startDate, endDate), // pptxgenjs is hiding bullet when empty string
        options: {
          bold: true,
          softBreakBefore: true
        }
      });
      if (summary) {
        const summaryTexts = summary.replace(/\r/g, "").split(/\n/);
        summaryTexts.forEach((text) =>
          output.push({
            text,
            options: {
              softBreakBefore: true
            }
          })
        );
      }

      (internalExperiences || []).map(({ name, description, metadata: { date_range } }) => {
        output.push({
          // pptxgenjs does not allow to position bullet points indented,
          // puts it always far left, this is a workaround
          text: `• ${formatExperienceText(formatDate, name, date_range?.start_date, date_range?.end_date)}`,
          options: {
            bold: true,
            softBreakBefore: true
          }
        });
        if (description) {
          const texts = description
            .replace(/\r/g, "")
            .split(/\n/)
            // pptxgenjs does not allow to add extra indentation here, this is a workaround
            .map((text) => `   ${text}`);

          texts.forEach((text) =>
            output.push({
              text,
              options: {
                softBreakBefore: true,
                //passed for getLinesCount to output correct result
                indent: internalExperienceDescriptionIndent
              }
            })
          );
        }
      });
    }
  );

  return limitLines(output, options);
};

export const createSummary = (summary: string, options: PptxgenJsTextOptions): TextItem[] => {
  const summaryArray = limitText(summary, options);

  return summaryArray.map((row, index) => ({
    text: row.text,
    options: {
      paraSpaceBefore: 0,
      softBreakBefore: true,
      breakLine: index === summaryArray.length - 1
    }
  }));
};

export const createContacts = (
  contactInformationItems: ContactInformationItem[],
  email: string
): TextItem[] => {
  const output: TextItem[] = [];
  [
    { text: email, options: textOptions },
    ...contactInformationItems.filter(({ type }) => /mobile_number/.test(type))
  ].map(({ text }) => {
    output.push({
      text,
      options: textOptions
    });
  });
  return output;
};

export const createSkills = (skillsType: CustomType, profile: ProfileExport, t): TextItem[] => {
  const coreSkills = filterDeselectedSkills(profile.skills?.filter(({ top }) => top) || [], skillsType)
    .map(({ text }) => text)
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const otherSkills = filterDeselectedSkills(profile.skills?.filter(({ top }) => !top) || [], skillsType)
    .slice(0, 9)
    .map(({ text }) => text)
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const coreSkillsContent: TextItem[] = coreSkills.map((item) => ({
    text: item,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));

  const otherSkillsContent: TextItem[] = otherSkills.map((item) => ({
    text: item,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));

  const output: TextItem[] = [];
  if (coreSkillsContent.length) {
    output.push(
      ...[
        EMPTY_LINE,
        {
          text: t("eyPptx.coreSkills"),
          options: {
            ...sidebarHeaderOptions,
            autoFit: true
          }
        },
        ...coreSkillsContent
      ]
    );
  }

  if (otherSkillsContent.length) {
    output.push(
      ...[
        EMPTY_LINE,
        {
          text: t("eyPptx.otherSkills"),
          options: {
            ...sidebarHeaderOptions,
            autoFit: true
          }
        },
        ...otherSkillsContent
      ]
    );
  }

  return output;
};

export const getAdjustedHeight = (text: string, options: PptxgenJsTextOptions) => {
  const textArray = limitText(text, options);
  const linesPerRow = textArray.map((item) => getLinesCount(item.text, { ...options, ...item.options }));
  const linesCount = linesPerRow.reduce((acc, curr) => acc + curr, 0);

  return Math.min(ptsToInch(linesCount * (options.lineSpacing || 10)), options.h || 0) + 2 * spaceSize;
};

export const createSidePanelTextContent = (
  data: ProfileExportFormatted,
  config,
  customTypes: CustomType[],
  order: string[],
  t: Function,
  translationKey: string,
  leftSidePanelTextOptions: PptxgenJsTextOptions
) => {
  const { contactInformationItems, email, name, id, jobTitle, _originalData } = data;
  const { isBlindModeOn } = config;

  const skillsFieldNames = ["skills"];
  const otherFieldNames = order.filter(
    (name) =>
      !["summary", "experience", "sectors", ...EY_CERTIFICATES_FIELD_NAMES, ...skillsFieldNames].includes(
        name
      )
  );

  const skillsType = customTypes.find(({ type }) => type.name === skillsFieldNames[0]);

  const leftDetailsTextArray: TextItem[] = [
    {
      text: isBlindModeOn ? `Candidate ${profileHash(id)}` : name,
      options: leftSidePanelTextOptions
    },
    {
      text: jobTitle || "",
      options: leftSidePanelTextOptions
    }
  ];

  if (!isBlindModeOn) {
    leftDetailsTextArray.push(...createContacts(contactInformationItems, email));
  }

  const skillsFields = skillsType ? createSkills(skillsType, _originalData, t) : [];
  skillsFields.length && leftDetailsTextArray.push(...skillsFields);

  otherFieldNames.forEach((name) => {
    const field = createCustomField(
      customTypes,
      [name],
      t(`${translationKey}.${name}`, humanize(capitalize(name))),
      leftSidePanelTextOptions
    );
    if (field.length) {
      leftDetailsTextArray.push(EMPTY_LINE);
      leftDetailsTextArray.push(...field);
    }
  });

  const allSectors = getSectors(data);
  if (allSectors.length) {
    leftDetailsTextArray.push(EMPTY_LINE);
    leftDetailsTextArray.push({
      text: t(`${translationKey}.sectors`),
      options: {
        ...sidebarHeaderOptions,
        autoFit: true
      }
    });
    leftDetailsTextArray.push(
      ...allSectors.map((item) => ({
        text: String(item.value),
        options: {
          breakLine: true,
          bullet: { indent: 9 }
        }
      }))
    );
  }
  // workaround: possible overflow
  return limitLines(leftDetailsTextArray, { ...leftSidePanelTextOptions, lineSpacing: 12 });
};
